/**
 * Track Hub — מסך האזור
 *
 * The landing screen inside any portal track (/learn, /pensia, /mashkanta):
 * a grid of topic tiles instead of an empty chat box. That is the whole point —
 * someone who doesn't know what to ask gets concrete places to start.
 *
 * Nothing on this page costs AI budget.
 */

import { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { AlertCircle, Info, MessageCircle } from 'lucide-react'
import TopicTile from '../../components/learning/TopicTile'
import LearningProgressBar from '../../components/learning/LearningProgressBar'
import { useLearningStore } from '../../store/learningStore'
import { useAuthStore } from '../../store/authStore'
import { TRACKS, type TrackKey } from '../../config/tracks'

interface TrackHubPageProps {
  track: TrackKey
}

export default function TrackHubPage({ track }: TrackHubPageProps) {
  const { user } = useAuthStore()
  const { trackInfo, topics, progress, allowance, topicsLoading, topicsError, fetchTopics } =
    useLearningStore()
  const headingRef = useRef<HTMLHeadingElement>(null)
  const config = TRACKS[track]

  useEffect(() => {
    void fetchTopics(track)
  }, [fetchTopics, track])

  // Land keyboard and screen-reader users at the top of the new content.
  useEffect(() => {
    headingRef.current?.focus()
  }, [track])

  const lessons = topics.filter((t) => !t.isExam)
  const examTopic = config.hasExam ? topics.find((t) => t.isExam) : undefined
  const examLocked = !progress?.examUnlocked
  const title = trackInfo?.name || config.name
  const intro = trackInfo?.heroText || config.tagline

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-6">
      <header>
        <h1
          ref={headingRef}
          tabIndex={-1}
          className="text-2xl font-bold text-gray-900 focus-visible:outline-none"
        >
          {title}
          {user?.firstName ? `, ${user.firstName}` : ''}
        </h1>
        <p className="mt-1 text-sm text-gray-700">{intro}</p>
      </header>

      {progress && progress.totalLessons > 0 && (
        <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-5">
          <LearningProgressBar
            completedCount={progress.completedCount}
            totalLessons={progress.totalLessons}
            percent={progress.percent}
          />
        </div>
      )}

      {allowance?.capReached && (
        <div className="mt-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <Info size={18} aria-hidden="true" className="mt-0.5 flex-shrink-0" />
          <p>{config.capReachedMessage}</p>
        </div>
      )}

      {topicsError && (
        <div
          className="mt-4 flex items-center gap-2 rounded-xl bg-red-50 p-4 text-sm text-red-800"
          role="alert"
        >
          <AlertCircle size={18} aria-hidden="true" />
          {topicsError}
        </div>
      )}

      {topicsLoading && topics.length === 0 && (
        <p className="mt-8 text-center text-sm text-gray-600">טוען את התוכן...</p>
      )}

      {/* An empty track is not a dead end: the chat still works, so say so. */}
      {!topicsLoading && topics.length === 0 && !topicsError && (
        <div className="mt-8 rounded-2xl border border-gray-200 bg-gray-50 p-6 text-center">
          <p className="text-sm text-gray-800">
            התוכן באזור הזה בהכנה — אבל אפשר כבר לשאול את {trackInfo?.name || config.name} כל שאלה.
          </p>
          <Link to={`${config.basePath}/chat`} className="btn btn-primary mt-4 inline-flex">
            <MessageCircle size={16} aria-hidden="true" className="ms-1" />
            פתיחת שיחה
          </Link>
        </div>
      )}

      {topics.length > 0 && (
        <section className="mt-6" aria-labelledby="topics-heading">
          <h2 id="topics-heading" className="sr-only">
            נושאי הלימוד
          </h2>
          <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {lessons.map((topic) => (
              <li key={topic.slug}>
                <TopicTile topic={topic} basePath={config.basePath} />
              </li>
            ))}
            {examTopic && (
              <li>
                <TopicTile
                  topic={examTopic}
                  basePath={config.basePath}
                  locked={examLocked}
                  lockHintId="exam-lock-hint"
                  bestScorePercent={progress?.bestScorePercent}
                />
              </li>
            )}
          </ul>

          {examTopic && examLocked && (
            <p id="exam-lock-hint" className="mt-3 text-sm text-gray-700">
              השאלון המסכם ייפתח אחרי שתסיים/י את כל {progress?.totalLessons ?? 5} הנושאים.
              {progress?.remainingTopics?.length
                ? ` נותרו: ${progress.remainingTopics.map((t) => t.title).join(', ')}.`
                : ''}
            </p>
          )}
        </section>
      )}

      {topics.length > 0 && (
        <section
          className="mt-8 rounded-2xl border border-gray-200 bg-gray-50 p-5"
          aria-labelledby="general-chat-heading"
        >
          <h2 id="general-chat-heading" className="text-base font-bold text-gray-900">
            יש לך שאלה שלא קשורה לנושא מסוים?
          </h2>
          <p className="mt-1 text-sm text-gray-700">
            {trackInfo?.chatIntro || 'אפשר לשאול כל שאלה בשיחה חופשית.'}
          </p>
          <Link to={`${config.basePath}/chat`} className="btn btn-secondary mt-3 inline-flex">
            <MessageCircle size={16} aria-hidden="true" className="ms-1" />
            שיחה כללית
          </Link>
        </section>
      )}

      <p className="mt-8 text-center text-xs text-gray-600">
        זהו מידע כללי בלבד. אין באמור ייעוץ אישי או המלצה לפעולה כלשהי.
      </p>
    </div>
  )
}
